import { useState, useCallback, useMemo } from 'react';
import {
  EMAIL, PRODUCT_TO_BE_ADDED, SEND_TO_PS, ADD_NOTE,
  DENY, EXTENDED_FLAG, EXTENDED_CONSULT, STOREFRONT_HISTORY,
  SOAP, PRODUCTS_COLUMNS, KIT, PRODUCT_TO_BE_CHANGED,
  HISTORY,
  NOTE_ANALYTICS,
  CRITICAL_NOTE,
  NOTE_ANALYTICS_BY_ORDER,
  PROMISE_STATUS,
  APPROVAL_TYPE,
  NOTE_ANALYTICS_LOADER,
  JODIT_EDITOR,
  JODIT_EDITOR_TEMPLATE_FOR_KIT,
  TASK_STATUS, PATIENT, VISIT_TYPE,
  JUMP_START_INFO,
} from '../../constants/patientChart.constant';
import { PatientChartProvider } from '../../contexts/patientChart';
import { Tabs, TabsCollection } from '../../components/patientChart/TabsWrapper/v1';
import { ColumnType, Order } from '../../components/subComponents/table';
import { CRITICAL_FLAG, CRITICAL_FLAGS, DOCTOR_FLAG, DOCTOR_FLAGS, FLAG_TYPE, ORDER_AND_PRESCRIBER_FLAGS } from '../../constants/visits.constant';
import { TASK_STATUS_TYPE } from '../../constants/tasks.constant';
import { ALTERNATE_RX_PRODUCT_VALUE } from '../../constants/product.constant';
import { getLoggedInUser } from '../../utils/jose';
import { getDateTime } from '../../utils/time';
import { isCancelledOrMedicallyDenied } from '../../utils/productStatus';
import { useFlagsValidation } from '../../hooks/useFlagsValidation';
import useQueryParams from '../../hooks/useQueryParams';

/**
 * @param {{
 *    productInfo: {
 *      StoreFrontData: {
 *        StorefrontApplicationSettings: Map<string, {
 *          key: string,
 *          value: string,
 *        }>,
 *      },
 *    };
 *    tasksInfoByOrderId: {
 *      called: boolean,
 *      loading: boolean,
 *      data: {
 *        Id: string,
 *      }[],
 *    };
 * }} props 
 */
const PatientChartController = (props) => {
  const {
    match,
    location,
    selectedPatientInfo: patient = PATIENT(),
    orderAndPrescriberFlagsV2 = ORDER_AND_PRESCRIBER_FLAGS(),
    criticalFlags: _criticalFlags = CRITICAL_FLAGS(),
    doctorFlags: _doctorFlags = DOCTOR_FLAGS(),
    changeProducts = {},
    productInfo,
    tasksInfoByOrderId: tasksInfo,

    GetProductInfoV2: getProduct = async () => ({}),
    GetChangeProductList: getChangeProducts = async () => {},
    GetEmailTemplateFromSalesforce: getEmailTemplatesFromSalesforce = async () => {},
    GetApproveEmailTemplatesFromSalesforce: getApproveEmailTemplatesFromSalesforce = async () => {},
    GetDenyEmailTemplatesFromSalesforce: getDenyEmailTemplatesFromSalesforce = async () => {},
    GetOrderAndPrescriberFlags: getOrderAndPrescriberFlags = async () => {},
    GetSoapNoteTemplates: getSoapTemplates = async () => {},
    GetNotesV2: getNotes = async () => ({}),
    GetApproveAndEmailPrepInfo: getApprovalInfo = async (requestBody = {}) => {},
    GetHistory: getHistory = async (requestBody = { OrderId: '' }) => {},
    GetDoseSpotV2: fetchJumpStartURL = async (requestBody = {}) => ({}),
    component: Component,
  } = props;
  const [ModalCollection, setModalCollection] = useState(new Map());
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [sortBy, setSortBy] = useState({
    key: PRODUCTS_COLUMNS[1].key,
    type: ColumnType.string,
    order: Order.asc
  });
  const [call, setCall] = useState({
    doctorNumber: '',
    error: new Map(),
    reason: -1,
    roomId: '',
  });
  const [joditEditorTemplate, setJoditEditorTemplate] = useState(JODIT_EDITOR());
  const [email, setEmail] = useState(EMAIL());
  const [message, setMessage] = useState({
    value: -1,
    template: { display: '' },
  });
  const [productToBeAdded, setProductToBeAdded] = useState(PRODUCT_TO_BE_ADDED());
  const [productToBeChanged, setProductToBeChanged] = useState(PRODUCT_TO_BE_CHANGED());
  const [sendToPS, setSendToPS] = useState(SEND_TO_PS());
  const [addNote, setAddNote] = useState(ADD_NOTE());
  const [deny, setDeny] = useState(DENY());
  const [soap, setSoap] = useState(SOAP());
  const [loading, setLoading] = useState(new Set(['']));
  const [flag, setFlag] = useState(EXTENDED_FLAG());
  const [extendedConsult, setExtendedConsult] = useState(EXTENDED_CONSULT());
  const [storefrontHistory, setStorefrontHistory] = useState(STOREFRONT_HISTORY());
  const [editableField, setEditableField] = useState(new Set(['']));
  const [joditEditorTemplateForKit, setJoditEditorTemplateForKit] = useState(JODIT_EDITOR_TEMPLATE_FOR_KIT());
  const [kit, setKit] = useState(KIT());
  const [pin, setPin] = useState('');
  const [noteAnalytics, setNoteAnalytics] = useState(NOTE_ANALYTICS_BY_ORDER());
  const [taskStatus, setTaskStatus] = useState(TASK_STATUS());
  const [icd10Code, setICD10Code] = useState('');
  /**
   * @type {[
   *  Map<string, string>,
   *  (props: Map<string, string>) => void
   * ]}
   */
  const [selectedICD10Codes, setSelectedICD10Codes] = useState(new Map());
  /**
   * @type {[
   *  Map<string, {
   *    Id: number;
   *    ICDCode: string;
   *    ICDDesc: string;
   *    OrderId: number;
   *  }>,
   *  (props: Map<string, {
   *    Id: number;
   *    ICDCode: string;
   *    ICDDesc: string;
   *    OrderId: number;
   *  }>) => void
   * ]}
   */
  const [removedICD10Codes, setRemovedICD10Codes] = useState(new Map());
  const [jumpStart, setJumpStart] = useState(JUMP_START_INFO());

  const { orderId: OrderId = 0, patientId: UserId = 0 } = useMemo(() => (match?.params || { patientId: 0 }), [match]);

  /**
   * @param {{
   *    tab?: string;
   * }} props
   * @returns {string}
   */
  const initTab = ({ tab }) => {
    const defaultTabKey = Tabs[0].key;

    if (!tab) {
      return defaultTabKey
    }
    
    const hasTab = TabsCollection.has(tab);
    if (hasTab) {
      const selectedTab = TabsCollection.get(tab);
      return selectedTab?.key;
    }

    return defaultTabKey;
  };

  /**
   * @description useQueryParams is used to read the version from query parameter
   */
  const { json: query } = useQueryParams({ location });
  const [tab, setTab] = useState(initTab({ tab: query?.tab }));

  /**
   * @param {{
   *    taskId?: string;
   * }} props
   * @returns {Set<string>}
   */
  const initCollapsible = ({ taskId }) => {
    /**
     * @type {Set<string>}
     */
    const items = new Set();
    if (taskId) {
      items.add(`${taskId}`);
    }
    return items;
  };

  const [collapsible, setCollapsible] = useState(initCollapsible({ taskId: query?.taskId || '' }));

  const { doctorFlags, criticalFlags } = useMemo(() => {
    let doctorFlags = [DOCTOR_FLAG()];
    doctorFlags = _doctorFlags.api.data;
    let criticalFlags = [CRITICAL_FLAG()];
    criticalFlags = _criticalFlags.api.data;

    return { criticalFlags, doctorFlags };
  }, [_criticalFlags, _doctorFlags]);

  const flags = useMemo(() => {
    let flags = ORDER_AND_PRESCRIBER_FLAGS();
    flags = orderAndPrescriberFlagsV2;
    return flags;
  }, [orderAndPrescriberFlagsV2]);

  const { orderFlags, prescriberFlags, assignedDoctorId, isAssignedDoctorViewing } = useMemo(() => {
    const { api: { data: { OrderFlagsV2, PrescriberFlags, DoctorId: assignedDoctorId = 0 } } } = flags;

    const prescriberFlags = (PrescriberFlags || []).map(({
      flagId,
      ProductId: productId,
      QuestionId: questionId,
      FlaggedAnswer: flaggedAnswer,
      FlaggedQuestion: flaggedQuestion,
      PrescriberFlagText: flagText,
    }) => ({
      flagId,
      questionId,
      productId,
      flagText,
      flaggedQuestion,
      flaggedAnswer,
      flagType: FLAG_TYPE.PRESCRIBER,
      required: true,
    }));

    const orderFlags = (OrderFlagsV2 || []).map((orderFlag) => ({
      ...orderFlag,
      productId: 0,
      questionId: 0,
      flagText: orderFlag?.flagHeader || orderFlag?.flagText,
      flaggedQuestion: '',
      flaggedAnswer: '',
      flagType: FLAG_TYPE.ORDER,
    }));

    const doctorId = getLoggedInUser().UserId;

    return {
      orderFlags,
      prescriberFlags,
      assignedDoctorId,
      isAssignedDoctorViewing: assignedDoctorId === Number(doctorId), // false,
    };
  }, [flags]);

  const { noOfRequiredFlags, noOfAnsweredFlags } = useFlagsValidation({
    orderFlags,
    prescriberFlags,
    flag: flag.current,
  });

  const areFlagsValid = useMemo(() => {
    if (!isAssignedDoctorViewing) {
      return false;
    }

    if (noOfRequiredFlags === 0) {
      return false;
    }

    if (noOfAnsweredFlags !== noOfRequiredFlags) {
      return false;
    }

    return true;
  }, [noOfRequiredFlags, noOfAnsweredFlags, isAssignedDoctorViewing]);

  const { noOfPendingTasks, noPendingTask } = useMemo(() => {
    if (!tasksInfo?.called) {
      return false;
    }

    if (tasksInfo?.loading) {
      return false;
    }

    const actualTasks = tasksInfo?.data?.filter((task) => {
      const notCompleted = task?.TaskStatus !== TASK_STATUS_TYPE.Complete;
      const fromCurrentOrder = Number(task?.OMSOrderId) === Number(OrderId);
      return notCompleted && fromCurrentOrder;
    });
    return {
      noOfPendingTasks: actualTasks.length,
      noPendingTask: actualTasks.length === 0
    };
  }, [OrderId, tasksInfo]);

  /**
   * @type {string}
   */
  const visitType = useMemo(() => {
    const { formatted: { time } } = getDateTime({ date_UTC: patient.StartDateTime_UTC });
    if (time) {
      return VISIT_TYPE.SCHEDULED;
    }
    return VISIT_TYPE.ASYNC;
  }, [patient]);

  const startLoading = useCallback((loading = new Set(['']), key = '') => {
    const __loading = new Set(loading);
    __loading.add(key);
    setLoading(__loading);
    return __loading;
  }, []);

  const stopLoading = useCallback((loading = new Set(['']), key = '') => {
    const __loading = new Set(loading);
    __loading.delete(key);
    setLoading(__loading);
    return __loading;
  }, []);

  const handleModalOpen = useCallback(({
    key = '',
    modal = {},
  }) => {
    const collection = new Map(ModalCollection);
    collection.set(key, modal);
    setModalCollection(collection);
  }, [ModalCollection]);

  const handleModalClose = useCallback(({
    key,
  }) => {
    const collection = new Map(ModalCollection);
    collection.delete(key);
    setModalCollection(collection);
  }, [ModalCollection]);

  const handleMultipleModalClose = useCallback(({
    keys = [],
  }) => {
    const collection = new Map(ModalCollection);
    keys.forEach((key) => {
      collection.delete(key);
    });
    setModalCollection(collection);
  }, [ModalCollection]);

  const handleExpandMultiple = useCallback(({
    keys = [],
  }) => {
    const _collapsible = new Set(collapsible);
    keys.forEach((key) => _collapsible.add(key));
    setCollapsible(_collapsible);
  }, [collapsible]);

  const handleCollapseMultiple = useCallback(({
    keys = [],
  }) => {
    const _collapsible = new Set(collapsible);
    keys.forEach((key) => _collapsible.delete(key));
    setCollapsible(_collapsible);
  }, [collapsible]);

  const isConfiguredAlternateRx = useCallback(({
    pId,
    isAlternateRx,
    skipCheckingChangeProducts = false,
    skipCheckingAppSettings = false,
  }) => {
    if (isAlternateRx) {
      return true;
    }

    if (pId === ALTERNATE_RX_PRODUCT_VALUE) {
      return true;
    }

    const productsCollection = new Map(changeProducts.api.data);
    if (productsCollection.has(pId) && skipCheckingChangeProducts === false) {
      /**
      * @type {{
      *    Value: number,
      * }[]}
      */
      const _changeProducts = productsCollection.get(pId);
      const hasAlternateRxProduct = _changeProducts.some(({ Value = 0 }) => (Value === ALTERNATE_RX_PRODUCT_VALUE));

      if (hasAlternateRxProduct) {
        return hasAlternateRxProduct;
      }
    }

    const settingsCollection = new Map(productInfo?.StoreFrontData?.StorefrontApplicationSettings);
    const hasAppSetting = settingsCollection.has('AlternateRxId');
    if (hasAppSetting && skipCheckingAppSettings === false) {
      /**
       * @type {{
       *  key: string;
       *  value: string;
       * }}
       */
      const setting = settingsCollection.get('AlternateRxId');
      const hasAlternateRxProduct = Number(setting.value) === ALTERNATE_RX_PRODUCT_VALUE;

      if (hasAlternateRxProduct) {
        return hasAlternateRxProduct;
      }
    }

    return false;
  }, [changeProducts, productInfo]);

  const handleProductResponse = async ({ data }) => {
    const selectedProducts = new Set();
    const { Product = [], OrderId = 0, StoreFrontData: { StorefrontId = 0 } } = data?.Data || { Product: [], OrderId: 0, StoreFrontData: { StorefrontId: 0 } };
    const ProductIds = [];
    /**
     * @type {number[]}
     */
    const PrescriptionProductIds = [];
    const ProductIdsAsQueryParameter = [];
    const PrescriptionProductIdsAsQueryParameter = [];
    const hasAlternateRx = Product.some((product) => isConfiguredAlternateRx({
      pId: product.ProductId,
      isAlternateRx: Boolean(product.IsAlternateRx),
      skipCheckingChangeProducts: true,
      skipCheckingAppSettings: true,
    }));
    for (let i = 0; i < Product.length; i++) {
      const product = Product[i];
      ProductIds.push(product.ProductId);
      ProductIdsAsQueryParameter.push(`productIds=${product.ProductId}`);
      /**
       * @description skip checking everything when the product's status is Cancelled. CRW-2743.
       */
      if (isCancelledOrMedicallyDenied({ status: product.Status })) {
        continue;
      }

      const key = `${product.ProductId}-${product.displayTitle}-${product.Qty}`;
      if (!hasAlternateRx) {
        selectedProducts.add(key);
      }

      if (!hasAlternateRx && !product.IsLabProduct) {
        PrescriptionProductIds.push(product.ProductId);
        PrescriptionProductIdsAsQueryParameter.push(`productIds=${product.ProductId}`);
      }

      const isAlternateRx = isConfiguredAlternateRx({
        pId: product.ProductId,
        isAlternateRx: Boolean(product.IsAlternateRx),
        skipCheckingChangeProducts: true,
        skipCheckingAppSettings: true,
      })
      if (hasAlternateRx && isAlternateRx) {
        selectedProducts.add(key);
        PrescriptionProductIds.push(product.ProductId);
        PrescriptionProductIdsAsQueryParameter.push(`productIds=${product.ProductId}`);
      }
    }
    // eslint-disable-next-line no-unused-vars
    const ProductIdsAsQueryString = `?${ProductIdsAsQueryParameter.join('&')}`;
    const PrescriptionProductIdsAsQueryString = `?${PrescriptionProductIdsAsQueryParameter.join('&')}`;

    setSelectedProducts(selectedProducts);
    getChangeProducts({ OrderId, ProductIds });
    getEmailTemplatesFromSalesforce({
      productIds: ProductIds,
      storefrontId: StorefrontId,
      action: 'Email',
      orderId: OrderId,
    });
    /**
     * @todo find a way to store the `AlternateRx` templates differently
     */
    getApproveEmailTemplatesFromSalesforce({
      productIds: ProductIds,
      storefrontId: StorefrontId,
      action: 'Approve',
      orderId: OrderId,
    });
    getDenyEmailTemplatesFromSalesforce({
      productIds: ProductIds,
      storefrontId: StorefrontId,
      action: 'Deny',
      orderId: OrderId,
    });
    getOrderAndPrescriberFlags({
      OrderId,
      productString: PrescriptionProductIdsAsQueryString,
    });
    getSoapTemplates({ productIds: ProductIds });

    if (UserId) {
      getApprovalInfo({
        PaitentId: UserId,
        OrderId,
        productString: PrescriptionProductIdsAsQueryString,
      });
    }
  };

  const handleLoadProductsListing = async ({ OrderId }) => {
    getProduct({ OrderId }, { optionalFunc: undefined, NoLoader: false }).then(handleProductResponse);
  };

  const handleLoadHistoryThenProductListing = async ({ OrderId }) => {
    /**
     * @description getProduct & getHistory responses will be combined for cmn template. We must use the "await" keyword here.
     */
    await getHistory({ OrderId }).then(handleHistoryResponse);
    handleLoadProductsListing({ OrderId });
  };

  const handleHistoryResponse = async ({ data, status }) => {
    return new Promise(async (next = () => {}) => {
      if (status !== 200) {
        return next(PROMISE_STATUS.rejected);
      }
  
      const _loading = new Set(loading);
      _loading.add(NOTE_ANALYTICS_LOADER);
      setLoading(_loading);
  
      let orders = [HISTORY()];
      orders = data?.Data || [];
  
      const noteAnalytics = new Map([
        [0, NOTE_ANALYTICS()],
      ]);
      noteAnalytics.delete(0);
  
      try {
        const promises = orders.map((order) => getNotes(order));
        const responses = await Promise.allSettled(promises);
        
        for (let i = 0; i < responses.length; i++) {
          const _r = responses[i];
          if (_r.status === 'rejected') {
            continue;
          }
  
          if (_r.value?.status !== 200) {
            continue;
          }
  
          let criticalNotes = [CRITICAL_NOTE()];
          criticalNotes = _r.value?.data?.Data?.CriticalNotes || [];
  
          let doctorNotes = [CRITICAL_NOTE()];
          doctorNotes = _r.value?.data?.Data?.DoctorNotes || [];
  
          const allNotes = [...criticalNotes, ...doctorNotes];
          const analytics = NOTE_ANALYTICS();
  
          for (let j = 0; j < allNotes.length; j++) {
            const _n = allNotes[j];
            if (typeof(_n?.NoteText) !== 'string') {
              continue;
            }
  
            const regex = /(this order disqualified)/gmi;
            if (regex.test(_n.NoteText)) {
              analytics.approval = APPROVAL_TYPE.disqualified;
            }
          }
  
          const order = orders[i];
          noteAnalytics.set(order.OrderId, analytics);
        }
  
        setNoteAnalytics(noteAnalytics);
      } catch(e) {
  
      }
  
      const __loading = new Set(_loading);
      __loading.delete(NOTE_ANALYTICS_LOADER);
      setLoading(__loading);
  
      return next(PROMISE_STATUS.fulfilled);
    });
  };

  return (
    <PatientChartProvider
      value={{
        orderId: Number(OrderId),
        patientId: Number(UserId),

        visitType,

        ModalCollection,
        setModalCollection,
        handleModalOpen,
        handleModalClose,
        handleMultipleModalClose,

        selectedProducts,
        setSelectedProducts,

        collapsible,
        handleExpandMultiple,
        handleCollapseMultiple,

        sortBy,
        setSortBy,

        tab,
        setTab,

        call,
        setCall,

        email,
        setMessage,

        joditEditorTemplate,
        setJoditEditorTemplate,

        joditEditorTemplateForKit,
        setJoditEditorTemplateForKit,

        message,
        setEmail,

        productToBeAdded,
        setProductToBeAdded,

        productToBeChanged,
        setProductToBeChanged,

        sendToPS,
        setSendToPS,

        addNote,
        setAddNote,

        deny,
        setDeny,

        soap,
        setSoap,

        taskStatus,
        setTaskStatus,

        jumpStart,
        setJumpStart,

        loading,
        setLoading,
        startLoading,
        stopLoading,

        flag,
        setFlag,

        extendedConsult,
        setExtendedConsult,

        storefrontHistory,
        setStorefrontHistory,

        editableField,
        setEditableField,

        kit,
        setKit,

        pin,
        setPin,

        icd10Code,
        setICD10Code,

        selectedICD10Codes,
        setSelectedICD10Codes,

        removedICD10Codes,
        setRemovedICD10Codes,

        noteAnalytics,
        setNoteAnalytics,

        orderFlags,
        prescriberFlags,
        doctorFlags,
        criticalFlags,
        assignedDoctorId,
        isAssignedDoctorViewing,
        areFlagsValid,
        noOfRequiredFlags,
        noOfAnsweredFlags,

        isConfiguredAlternateRx,

        noOfPendingTasks,
        noPendingTask,

        fetchJumpStartURL,

        handleLoadProductsListing,
        handleHistoryResponse,
        handleLoadHistoryThenProductListing,
      }}
    >
      <Component {...props} />
    </PatientChartProvider>
  );
};

export const PatientChartControllerHOC = (Component) => (props) => (
  <PatientChartController component={Component} {...props} />
);

export default PatientChartControllerHOC;
